/**
 * Storage Utilities - Selective localStorage cleanup
 * 
 * Provides safe methods to clear application cache without affecting:
 * - Authentication tokens (jwt_token, jwt_user)
 * - Business data (businessLogo, selectedPlan)
 * - Third-party integrations (canva_access_token)
 */

/**
 * Clears player cache and device-related localStorage keys
 * while preserving authentication and critical business data
 */
export const clearPlayerCache = (): void => {
    // Static keys to remove
    const staticKeysToRemove = [
        // Player & Device
        'deviceCode',
        'player_device_id',
        'player_cache_user_id',
        'deviceId',
        'player_brightness',
        'displayTimeChanged',

        // Media Cache
        'player_screens',
        'player_medias',
        'pending_media_events',

        // Settings
        'showWeatherClock',
        'showNewsTicker',
        'brightness',
    ];

    // Remove static keys
    staticKeysToRemove.forEach(key => {
        try {
            localStorage.removeItem(key);
        } catch (error) {
            console.warn(`Failed to remove localStorage key: ${key}`, error);
        }
    });

    // Remove dynamic keys (keys with user IDs or patterns)
    try {
        Object.keys(localStorage).forEach(key => {
            if (
                key.startsWith('device_activation_') ||
                key.startsWith('news_cache_') ||
                key.startsWith('news_cache_time_') ||
                key.startsWith('lastPromoCheck_') ||
                key.endsWith('_devices')
            ) {
                localStorage.removeItem(key);
            }
        });
    } catch (error) {
        console.warn('Failed to remove dynamic localStorage keys', error);
    }

    console.log('✅ Player cache cleared successfully');
};

/**
 * Clears ALL application data including authentication
 * Use this ONLY for complete logout/reset scenarios
 */
export const clearAllAppData = (): void => {
    try {
        // Get all keys
        const allKeys = Object.keys(localStorage);

        // Remove all app-related keys but preserve potential third-party data
        allKeys.forEach(key => {
            localStorage.removeItem(key);
        });

        console.log('✅ All application data cleared');
    } catch (error) {
        console.error('Failed to clear application data', error);
    }
};

/**
 * Legacy function for backward compatibility
 * @deprecated Use clearPlayerCache() instead
 */
export const legacyClearStorage = clearPlayerCache;
